import { type ReactElement, useMemo } from 'react'
import Decimal from 'decimal.js'
import { find } from 'lodash-es'
import { Stack } from '@mui/material'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { Section } from '@ui2/structure'
import { useModelStore, useSystemManagerStore } from '@editorStores'
import AbsolutePositionBlock from './components/AbsolutePositionBlock'
import AssemblyBlock from './components/AssemblyBlock'

const ABS_POSITION_NUM_DECIMALS = 2

export type SystemSectionProps = {
  surfaceGuid: string
  beamGuid: string
  elementType: 'slab_beams' | 'roof_slab_beams'
  disableForm?: boolean
}

const SystemSection = ({
  surfaceGuid,
  beamGuid,
  elementType,
  disableForm = false,
}: SystemSectionProps): ReactElement => {
  const systemSection = useResultsSectionOpen('results2.surface.system')
  const assemblyElementType = elementType === 'roof_slab_beams' ? 'roof_slabs' : 'slabs'

  const assemblyAssignment = useSystemManagerStore(state => state.assemblyAssignment)
  const selectedAssemblyGuid = useMemo(
    () => find(assemblyAssignment, ['element_guid', surfaceGuid])?.assembly_guid ?? null,
    [assemblyAssignment, surfaceGuid],
  )

  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const slab = useMemo(
    () => find([...verticalSlabs, ...verticalRoofSlabs], { guid: surfaceGuid }),
    [verticalSlabs, verticalRoofSlabs, surfaceGuid],
  )

  const slabLength = useMemo(
    () => (slab ? slab.shape.points[0].distanceTo(slab.shape.points[1]) : 0),
    [slab],
  )

  const relativePositionDefault = useMemo(() => {
    if (!slab?.beam) return '0'
    const absolutePosition = slab.shape.points[0].distanceTo(slab.beam.shape.start)
    const absolutePositionRounded = absolutePosition.toFixed(ABS_POSITION_NUM_DECIMALS)
    const slabLengthRounded = slabLength.toFixed(ABS_POSITION_NUM_DECIMALS)
    return new Decimal(absolutePositionRounded).div(new Decimal(slabLengthRounded)).toString()
  }, [slab, slabLength])

  return (
    <Section title="System" collapsible hideBorderBottom {...systemSection}>
      <Stack spacing={1.5} sx={{ pt: 1 }}>
        <AssemblyBlock
          key={`${surfaceGuid}:${selectedAssemblyGuid ?? ''}`}
          surfaceGuid={surfaceGuid}
          assemblyElementType={assemblyElementType}
          selectedAssemblyGuid={selectedAssemblyGuid}
          disableForm={disableForm}
        />
        <AbsolutePositionBlock
          key={`${beamGuid}:${relativePositionDefault}`}
          beamGuid={beamGuid}
          elementType={elementType}
          slabLength={slabLength}
          relativePositionDefault={relativePositionDefault}
          disableForm={disableForm}
        />
      </Stack>
    </Section>
  )
}

export default SystemSection
